import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit3, X, Save, Loader2, Link2, Twitter, Facebook } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/sobs";
import { mediaUrl } from "../lib/content";
import { TextField, TextArea, SelectField, ImageField, inputCls, labelCls } from "./fields";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function SocialManager() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/social").then((r) => setPosts(r.data)).catch(() => toast.error("Failed to load"));
  useEffect(() => { load(); }, []);

  const save = async (d) => {
    if (!d.text.trim()) { toast.error("Post text is required"); return; }
    if (!d.url.trim()) { toast.error("Link to the original post is required"); return; }
    const payload = { ...d, posted_at: d.posted_at ? new Date(d.posted_at).toISOString() : undefined };
    try {
      if (d.id) await api.put(`/admin/social/${d.id}`, payload); else await api.post("/admin/social", payload);
      toast.success("Saved — live on the website"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this post from the website feed?")) return;
    try { await api.delete(`/admin/social/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="social-manager">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FF5722]">Engagement</div>
          <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-[#0B2545]">Social Feed</h1>
          <p className="mt-1 text-sm text-[#6B7280]">Curate the "From the SoBS feed" section. Paste an X post link to auto-fill, or enter a Facebook post manually.</p>
        </div>
        <button onClick={() => setEditing({ platform: "x", url: "", text: "", image_url: "", author: "SoBS Somalia", posted_at: new Date().toISOString().slice(0, 10), status: "published" })} className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.14em] bg-[#0B2545] text-white rounded-sm hover:bg-[#081C36]" data-testid="add-social-btn"><Plus size={14} /> Add post</button>
      </div>

      <div className="bg-white border border-black/10 rounded-sm divide-y divide-black/5">
        {posts === null && <div className="p-10 text-center text-sm text-[#6B7280]">Loading…</div>}
        {posts && posts.length === 0 && <div className="p-10 text-center text-sm text-[#6B7280]" data-testid="social-empty">No posts yet — the website shows a "Follow us" banner instead.</div>}
        {(posts || []).map((p) => (
          <div key={p.id} className="p-4 sm:p-5 flex items-start gap-4" data-testid={`admin-social-row-${p.id}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0 ${p.platform === "facebook" ? "bg-[#1877F2]" : "bg-black"}`}>{p.platform === "facebook" ? <Facebook size={15} /> : <Twitter size={15} />}</div>
            {p.image_url && <img src={mediaUrl(p.image_url)} alt="" className="w-16 h-16 object-cover rounded-sm flex-shrink-0 hidden sm:block" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.16em] font-bold">
                <span className="text-[#FF5722]">{p.platform === "facebook" ? "Facebook" : "X"}</span>
                <span className={`px-2 py-0.5 rounded-sm ${p.status === "draft" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{p.status}</span>
                <span className="text-[#6B7280] font-medium">{fmt(p.posted_at)} · {p.author}</span>
              </div>
              <p className="mt-1.5 text-sm text-[#0B2545] line-clamp-2">{p.text}</p>
            </div>
            <button onClick={() => setEditing({ ...p, posted_at: (p.posted_at || "").slice(0, 10) })} className="p-2 hover:bg-black/5 rounded-sm" data-testid={`edit-social-${p.id}`}><Edit3 size={15} /></button>
            <button onClick={() => del(p.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-sm" data-testid={`delete-social-${p.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>

      {editing && <PostEditor item={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function PostEditor({ item, onClose, onSave }) {
  const [d, setD] = useState(item);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setD((x) => ({ ...x, [k]: v }));

  const fetchPost = async () => {
    if (!d.url.trim()) { toast.error("Paste the post link first"); return; }
    setFetching(true);
    try {
      const r = await api.post("/admin/social/fetch", { url: d.url });
      setD((x) => ({ ...x, platform: r.data.platform, text: r.data.text || x.text, author: r.data.author || x.author, posted_at: r.data.posted_at ? r.data.posted_at.slice(0, 10) : x.posted_at }));
      if (r.data.note) toast.info(r.data.note); else toast.success("Post details fetched");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fetch failed"); }
    setFetching(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="social-editor-modal">
      <div className="bg-white max-w-2xl w-full p-6 sm:p-8 my-6 relative rounded-sm">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-black/5 rounded-sm" data-testid="social-editor-close"><X size={18} /></button>
        <h2 className="font-display font-black text-2xl tracking-tighter text-[#0B2545]">{item.id ? "Edit" : "Add"} social post</h2>
        <div className="mt-6 grid gap-5">
          <div>
            <label className={labelCls}>Link to the original post</label>
            <div className="mt-1.5 flex gap-2">
              <input value={d.url} onChange={(e) => set("url")(e.target.value)} placeholder="https://x.com/SobsSomalia/status/… or https://www.facebook.com/sobs.gov.so/posts/…" className={`${inputCls} !mt-0 flex-1`} data-testid="sp-url" />
              <button type="button" onClick={fetchPost} disabled={fetching} className="inline-flex items-center gap-2 px-3.5 text-xs font-bold uppercase tracking-[0.12em] bg-[#FFC107] text-[#0B2545] rounded-sm whitespace-nowrap disabled:opacity-60" data-testid="sp-fetch">{fetching ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} Fetch</button>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <SelectField label="Platform" value={d.platform} onChange={set("platform")} options={[{ value: "x", label: "X (Twitter)" }, { value: "facebook", label: "Facebook" }]} testid="sp-platform" />
            <TextField label="Account name" value={d.author} onChange={set("author")} testid="sp-author" />
            <TextField label="Post date" type="date" value={d.posted_at} onChange={set("posted_at")} testid="sp-date" />
          </div>
          <TextArea label="Post text" rows={5} value={d.text} onChange={set("text")} testid="sp-text" />
          <ImageField label="Photo (optional)" value={d.image_url} onChange={set("image_url")} testid="sp-image" />
          <SelectField label="Status" value={d.status} onChange={set("status")} options={[{ value: "published", label: "Published (live)" }, { value: "draft", label: "Draft (hidden)" }]} testid="sp-status" />
          <div className="flex gap-3 pt-2">
            <button onClick={async () => { setSaving(true); await onSave(d); setSaving(false); }} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] bg-[#0B2545] text-white rounded-sm disabled:opacity-60" data-testid="sp-save">{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save & publish</button>
            <button onClick={onClose} className="px-4 py-2.5 text-xs uppercase tracking-[0.14em] font-bold text-[#6B7280]" data-testid="sp-cancel">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
